import { CONVERSATION_TYPE } from "@/components/Chat/viewModel";
import { MessageList } from "@/components/Notify";
import { TrackerDiv } from "@/components/Tracker";
import useAppStore from "@/context/store";
import {
  getConversationList,
  setDMAllReaded,
  setSystemDMReaded,
} from "@/services";
import getTimeDuration from "@/utils/getTimeDuration";
import { cleanHtmlTag } from "@/utils/str";
import useRequest from "@/utils/useReq";
import { useAsyncEffect, useSafeState, useSetState } from "ahooks";
import { Badge, Button, Dropdown, Tabs } from "antd";
import classNames from "classnames";
import Link from "next/link";
import { useEffect } from "react";
import HBAvatar from "../../Avatar";
import HBIcon from "../../HBIcon";
import styles from "./index.module.less";

export default function NotificationCenter() {
  const { unreadNum, dmUnread } = useAppStore((state) => ({
    unreadNum: state.unreadNum,
    dmUnread: state.dmUnread,
  }));
  const { unread_dm = 0 } = dmUnread || {};
  const totalUnread = unreadNum + unread_dm;

  return (
    <Dropdown
      overlay={<NotifyDrop unreadNum={unreadNum} dmUnread={dmUnread} />}
      placement="bottomRight"
      destroyPopupOnHide
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
      mouseEnterDelay={0.3}
      mouseLeaveDelay={0.3}
    >
      <Button
        className={styles.headerItem}
        icon={
          <Badge dot={totalUnread > 0}>
            <HBIcon type="ic_notifications" />
          </Badge>
        }
        data-gd-expose="button_expose"
        data-gd-click="button_click"
        data-subject-name="按钮"
        data-button-name="消息"
        data-module-name="顶部导航栏"
      />
    </Dropdown>
  );
}

function NotifyDrop({ unreadNum, dmUnread }) {
  const { unread_dm = 0 } = dmUnread || {};
  const [defaultActiveKey] = useSafeState(
    unreadNum > 0 && unread_dm === 0 ? "message" : "dm"
  );
  const [chatProps, setChatProps] = useSetState({
    visible: false,
    targetUserId: null,
  });

  const [hasReadList, setHasReadList] = useSafeState([]);

  useAsyncEffect(async () => {
    if (chatProps.visible) {
      const { ChatDialog } = await import("@/components/Chat");
      ChatDialog.open({
        ...chatProps,
        onCancel() {
          setChatProps({ visible: false });
        },
      });
    }
  }, [chatProps, setChatProps]);

  useEffect(() => {
    if (hasReadList.includes(chatProps.targetUserId)) return;
    setHasReadList([...hasReadList, chatProps.targetUserId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatProps?.targetUserId]);

  const { data: conversationList, refresh: refreshConversationList } =
    useRequest(async () => {
      const res = await getConversationList({
        limit: 100,
      });
      const unreadList = res.conversations.filter((item) => item.unread);
      const readList = res.conversations.filter((item) => !item.unread);
      return [...unreadList, ...readList];
    }, []);

  const systemAvatar =
    "https://cdn.dancf.com/fe-assets/img/cd8cef63868baa9debd5eefafa7d93af.png";

  return (
    <TrackerDiv
      className={styles.notify}
      data-gd-expose="dialog_expose"
      data-module-name="消息通知弹窗"
      data-subject-name="弹窗"
      data-dialog-title="消息"
    >
      <Tabs defaultActiveKey={defaultActiveKey}>
        <Tabs.TabPane
          tab={
            <div className={styles.tabTitle}>
              <Badge count={unread_dm} overflowCount={99}>
                私信
              </Badge>
            </div>
          }
          key="dm"
          className={styles.tabPane}
        >
          {conversationList?.length > 0 && (
            <div className={styles.conversationList}>
              {conversationList.map((record, idx) => {
                const { with_user, last_message, unread, text, created_at } =
                  record;

                return (
                  <div
                    key={idx}
                    className={classNames(styles.conversationItem, {
                      [styles.hasNews]:
                        unread &&
                        !hasReadList.includes(
                          with_user?.user_id || CONVERSATION_TYPE.SYSTEM
                        ),
                    })}
                    onClick={() =>
                      setChatProps({
                        visible: true,
                        targetUserId:
                          with_user?.user_id || CONVERSATION_TYPE.SYSTEM,
                      })
                    }
                  >
                    {with_user ? (
                      <>
                        <HBAvatar
                          className={styles.conversationAvatar}
                          user={with_user}
                          shape="circle"
                          size={32}
                        />
                        <div className={styles.info}>
                          <div className={styles.name}>
                            {with_user.username}
                          </div>
                          <div
                            className={styles.message}
                            title={last_message.text}
                          >
                            {last_message.text.substring(0, 19) +
                              (last_message.text?.length > 19 ? "..." : "")}
                            <span className={styles.time}>
                              {getTimeDuration({
                                time: last_message.created_at * 1000,
                              })}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <HBAvatar
                          className={styles.conversationAvatar}
                          imgUrl={systemAvatar}
                          shape="circle"
                          size={32}
                        />
                        <div className={styles.info}>
                          <div className={styles.name}>系统消息</div>
                          <div className={styles.message}>
                            {cleanHtmlTag(text).substring(0, 19) + "..."}
                            <span className={styles.time}>
                              {getTimeDuration({ time: created_at * 1000 })}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {conversationList?.length === 0 && (
            <div className={styles.empty}>还没有私信消息哦 ~</div>
          )}
          <div
            className={styles.bottom}
            style={{ justifyContent: "space-between" }}
          >
            <a
              onClick={async () => {
                await setSystemDMReaded();
                await setDMAllReaded();
                refreshConversationList();
                useAppStore.setState({
                  dmUnread: { unread_system_dm: 0, unread_dm: 0 },
                });
              }}
            >
              <span>全部已读</span>
            </a>
            <a onClick={() => setChatProps({ visible: true })}>
              <span>
                查看所有私信 <HBIcon type="ic_chevron_right" />
              </span>
            </a>
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <div className={styles.tabTitle}>
              <Badge count={unreadNum} overflowCount={99}>
                动态
              </Badge>
            </div>
          }
          key="message"
          className={styles.tabPane}
        >
          <MessageList
            wrapId={"message-wrap"}
            type={"all"}
            height={283}
            limit={20}
            avatarSize={32}
            onLoad={() => {
              useAppStore.setState({
                unreadNum: 0,
              });
            }}
            itemStyle={{ margin: "2px 20px 10px 0", padding: "0" }}
          />
          <div className={styles.bottom}>
            <Link href={"/message"}>
              <span>
                查看所有动态 <HBIcon type="ic_chevron_right" />
              </span>
            </Link>
          </div>
        </Tabs.TabPane>
      </Tabs>
      {/* <ChatModal
        {...chatProps}
        onCancel={() => setChatProps({ visible: false })}
      /> */}
    </TrackerDiv>
  );
}
